import PurgePreviewPopup from '@/features/purge/components/PurgePreviewPopup';
import { usePurgePreview, useRunPurge } from '@/features/purge/hooks/usePurge';
import {
  MANAGED_RULE_NAME,
  OLDER_THAN_DAY_CHOICES,
  SIMPLE_RULE_DEFAULTS,
  cleanupInput,
  type LabelMatch,
} from '@/features/purge/purge-settings';
import {
  DAY_OF_MONTH_CHOICES,
  WEEKDAY_CHOICES,
  scheduleDayFor,
} from '@/features/purge/purge-rule-form';
import { ChipInput } from '@/components/ChipInput';
import ChipMultiSelect from '@/components/ChipMultiSelect';
import { SettingIcon } from '@/components/SettingIcon';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { safeLabelColor } from '@/features/labels/utils/label-color';
import { apiErrorText } from '@/api/errors';
import { useErrorT, useT } from '@/i18n';
import type {
  AgeBasis,
  CreatePurgeRuleInput,
  Label,
  PurgeRule,
  PurgeTarget,
  UpdatePurgeRuleInput,
} from '@restaurant/api-contracts';
import {
  AccordionContent,
  BlockFooter,
  BlockTitle,
  Chip,
  List,
  ListItem,
  Popover,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { useEffect, useRef, useState } from 'react';

interface Props {
  target: PurgeTarget;
  rule: PurgeRule | undefined;
  extraRuleCount: number;
  labels: Label[];
  onCreate: (body: CreatePurgeRuleInput) => Promise<PurgeRule>;
  onUpdate: (args: { ruleId: string; data: UpdatePurgeRuleInput }) => Promise<PurgeRule>;
}

function sameDays(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((d) => set.has(d));
}

function PurgeTargetSection({ target, rule, extraRuleCount, labels, onCreate, onUpdate }: Props) {
  const t7 = useT();
  const errorT = useErrorT();
  const t = t7.purge;
  const adv = t.advanced;
  const copy = target === 'ORDER' ? t.orders : t.reservations;
  const runPurge = useRunPurge();

  const [olderThanDays, setOlderThanDays] = useState(rule?.olderThanDays ?? 365);
  const [labelIds, setLabelIds] = useState<string[]>(rule?.labels.map((l) => l.id) ?? []);
  const [auto, setAuto] = useState(rule?.enabled ?? false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [ageBasis, setAgeBasis] = useState<AgeBasis>(
    rule?.ageBasis ?? SIMPLE_RULE_DEFAULTS.ageBasis,
  );
  const [labelMatch, setLabelMatch] = useState<LabelMatch>(
    rule?.labelMatch ?? SIMPLE_RULE_DEFAULTS.labelMatch,
  );
  const [scheduleKind, setScheduleKind] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>(
    rule?.scheduleKind ?? SIMPLE_RULE_DEFAULTS.scheduleKind,
  );
  const [scheduleDay, setScheduleDay] = useState<number[]>(rule?.scheduleDay ?? []);
  const [scheduleHour, setScheduleHour] = useState(
    rule?.scheduleHour ?? SIMPLE_RULE_DEFAULTS.scheduleHour,
  );

  const running = useRef(false);

  useEffect(() => {
    if (!rule) return;
    setOlderThanDays(rule.olderThanDays);
    setLabelIds(rule.labels.map((l) => l.id));
    setAuto(rule.enabled);
    setAgeBasis(rule.ageBasis);
    setLabelMatch(rule.labelMatch);
    setScheduleKind(rule.scheduleKind);
    setScheduleDay(rule.scheduleDay);
    setScheduleHour(rule.scheduleHour);
  }, [rule]);

  const input =
    labelIds.length > 0
      ? cleanupInput(target, olderThanDays, labelIds, ageBasis, labelMatch)
      : null;
  const { data: preview, isFetching, isError } = usePurgePreview(input);

  const toast = (text: string) => f7.toast.create({ text, closeTimeout: 3000 }).open();

  function openPicker(
    title: string,
    options: { text: string; bold: boolean; onClick: () => void }[],
  ) {
    f7.actions
      .create({
        buttons: [[{ label: true, text: title }, ...options, { text: t.close, color: 'red' }]],
      })
      .open();
  }

  interface RuleFields {
    enabled: boolean;
    olderThanDays: number;
    labelIds: string[];
    ageBasis: AgeBasis;
    labelMatch: LabelMatch;
    scheduleKind: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    scheduleDay: number[];
    scheduleHour: number;
  }

  function fields(overrides: Partial<RuleFields> = {}): RuleFields {
    const merged = {
      enabled: auto,
      olderThanDays,
      labelIds,
      ageBasis,
      labelMatch,
      scheduleKind,
      scheduleDay,
      scheduleHour,
      ...overrides,
    };
    merged.scheduleDay = scheduleDayFor(merged.scheduleKind, merged.scheduleDay);
    return merged;
  }

  async function persist(next: RuleFields) {
    if (next.enabled && next.labelIds.length === 0) {
      toast(t.pickATagFirst);
      return;
    }
    if (next.enabled && next.scheduleKind !== 'DAILY' && next.scheduleDay.length === 0) {
      toast(t.dayRequired);
      return;
    }
    try {
      if (rule) {
        await onUpdate({ ruleId: rule.id, data: next });
      } else {
        await onCreate({ name: MANAGED_RULE_NAME[target], target, ...next });
      }
    } catch (e) {
      toast(apiErrorText(e, errorT, t.couldNotSave));
    }
  }

  function withZeroDayGuard(next: RuleFields, proceed: () => void) {
    if (next.enabled && next.olderThanDays === 0) {
      f7.dialog.confirm(t.zeroDayAutoBody, t.zeroDayAutoTitle, proceed);
    } else {
      proceed();
    }
  }

  function persistIfScheduled(overrides: Partial<RuleFields>) {
    if (auto) void persist(fields(overrides));
  }

  function changeAge(days: number) {
    setOlderThanDays(days);
    if (!auto) return;
    const next = fields({ olderThanDays: days });
    withZeroDayGuard(next, () => void persist(next));
  }

  function toggleLabel(labelId: string) {
    const next = labelIds.includes(labelId)
      ? labelIds.filter((id) => id !== labelId)
      : [...labelIds, labelId];
    setLabelIds(next);
    persistIfScheduled({ labelIds: next });
  }

  function toggleAuto(value: boolean) {
    if (value && labelIds.length === 0) {
      toast(t.pickATagFirst);
      return;
    }
    const next = fields({ enabled: value });
    withZeroDayGuard(next, () => {
      setAuto(value);
      void persist(next);
    });
  }

  function changeAgeBasis(value: string) {
    const next: AgeBasis = value === 'SERVICE_TIME' ? 'SERVICE_TIME' : 'CREATED_AT';
    setAgeBasis(next);
    persistIfScheduled({ ageBasis: next });
  }

  function changeLabelMatch(value: string) {
    const next: LabelMatch = value === 'ALL' ? 'ALL' : 'ANY';
    setLabelMatch(next);
    persistIfScheduled({ labelMatch: next });
  }

  function changeScheduleDay(days: number[]) {
    setScheduleDay(days);
    persistIfScheduled({ scheduleDay: days });
  }

  function toggleMonthDay(day: number) {
    setScheduleDay((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  }

  function commitMonthDays() {
    if (!auto) return;
    if (sameDays(scheduleDay, rule?.scheduleDay ?? [])) return;
    if (scheduleDay.length === 0) {
      toast(t.dayRequired);
      return;
    }
    void persist(fields({ scheduleDay }));
  }

  function changeScheduleHour(hour: number) {
    setScheduleHour(hour);
    persistIfScheduled({ scheduleHour: hour });
  }

  function changeScheduleKind(kind: 'DAILY' | 'WEEKLY' | 'MONTHLY') {
    const day = scheduleDayFor(kind, scheduleDay);
    setScheduleKind(kind);
    setScheduleDay(day);
    persistIfScheduled({ scheduleKind: kind, scheduleDay: day });
  }

  function countLine(): string {
    if (!input) return t.pickATagToCount;
    if (isError) return t.couldntCount;
    if (isFetching || !preview) return t.counting;
    return copy.wouldHide(preview.count);
  }

  const count = preview?.count ?? 0;
  const ready = input !== null && !isFetching && !isError && count > 0;

  async function run() {
    if (!ready || !input || running.current) return;
    running.current = true;
    try {
      const purgeRun = await runPurge.mutateAsync({
        input,
        ruleId: auto ? rule?.id : undefined,
        expectedCount: count,
      });
      toast(copy.hidden(purgeRun.affectedCount, count));
    } catch (e) {
      toast(apiErrorText(e, errorT, t.couldNotSave));
    } finally {
      running.current = false;
    }
  }

  function confirmAndRun() {
    if (!ready || running.current) return;
    f7.dialog.confirm(copy.confirmBody(count), t.confirmTitle, () => void run());
  }

  if (extraRuleCount > 0) {
    return (
      <>
        <BlockTitle>{copy.title}</BlockTitle>
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
          <ListItem title={t.managedInAdvanced} />
        </List>
        <BlockFooter>{t.managedInAdvancedFooter(extraRuleCount)}</BlockFooter>
      </>
    );
  }

  return (
    <>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          title={copy.olderThanLabel}
          after={t.olderThanOption(olderThanDays)}
          link="#"
          onClick={() =>
            openPicker(
              copy.olderThanLabel,
              OLDER_THAN_DAY_CHOICES.map((days) => ({
                text: t.olderThanOption(days),
                bold: days === olderThanDays,
                onClick: () => changeAge(days),
              })),
            )
          }
        />
      </List>

      <BlockTitle>{t.cleanupTagsTitle}</BlockTitle>
      <BlockFooter>{t.cleanupSomeTags}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {labels.length === 0 && <ListItem title={t.noLabels} />}
        {labels.map((label) => (
          <ListItem
            key={label.id}
            title={label.name}
            checkbox
            checked={labelIds.includes(label.id)}
            onChange={() => toggleLabel(label.id)}
          >
            <Chip
              slot="after"
              text=" "
              style={{ backgroundColor: safeLabelColor(label.color), width: 18 }}
            />
          </ListItem>
        ))}
      </List>

      <BlockFooter>
        {countLine()}
        {ready && (
          <>
            {' '}
            <F7Link onClick={() => setPreviewOpen(true)}>{t.seeThem}</F7Link>
          </>
        )}
      </BlockFooter>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          title={runPurge.isPending ? t.working : copy.hideAction(count)}
          link="#"
          onClick={ready && !runPurge.isPending ? confirmAndRun : undefined}
          style={{ color: ready ? '#ff3b30' : '#8e8e93' }}
        >
          <div slot="media">
            <SettingIcon icon="tray_2_fill" color={ready ? '#ff3b30' : '#8e8e93'} />
          </div>
        </ListItem>
      </List>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.autoTitle}>
          <ToggleSwitch slot="after" checked={auto} onChange={toggleAuto} />
        </ListItem>
      </List>
      <BlockFooter>{labelIds.length === 0 ? t.pickATagFirst : t.autoOnFooter}</BlockFooter>

      <List
        strongIos
        strongMd
        insetIos
        insetMd
        dividersIos
        dividersMd
        accordionList
        style={{ marginTop: 24 }}
      >
        <ListItem accordionItem title={t.advancedTitle}>
          <AccordionContent>
            <List dividersIos dividersMd>
              <ListItem
                title={adv.ageBasisLabel}
                after={ageBasis === 'CREATED_AT' ? adv.ageBasisCreated : adv.ageBasisService}
                link="#"
                onClick={() =>
                  openPicker(adv.ageBasisLabel, [
                    {
                      text: adv.ageBasisCreated,
                      bold: ageBasis === 'CREATED_AT',
                      onClick: () => changeAgeBasis('CREATED_AT'),
                    },
                    {
                      text: adv.ageBasisService,
                      bold: ageBasis === 'SERVICE_TIME',
                      onClick: () => changeAgeBasis('SERVICE_TIME'),
                    },
                  ])
                }
              />

              {labelIds.length > 1 && (
                <ListItem
                  title={adv.matchLabel}
                  after={labelMatch === 'ANY' ? adv.matchAny : adv.matchAll}
                  link="#"
                  onClick={() =>
                    openPicker(adv.matchLabel, [
                      {
                        text: adv.matchAny,
                        bold: labelMatch === 'ANY',
                        onClick: () => changeLabelMatch('ANY'),
                      },
                      {
                        text: adv.matchAll,
                        bold: labelMatch === 'ALL',
                        onClick: () => changeLabelMatch('ALL'),
                      },
                    ])
                  }
                />
              )}

              <ListItem
                title={adv.kindLabel}
                after={
                  scheduleKind === 'DAILY'
                    ? adv.kindDaily
                    : scheduleKind === 'WEEKLY'
                      ? adv.kindWeekly
                      : adv.kindMonthly
                }
                link="#"
                onClick={() =>
                  openPicker(adv.kindLabel, [
                    {
                      text: adv.kindDaily,
                      bold: scheduleKind === 'DAILY',
                      onClick: () => changeScheduleKind('DAILY'),
                    },
                    {
                      text: adv.kindWeekly,
                      bold: scheduleKind === 'WEEKLY',
                      onClick: () => changeScheduleKind('WEEKLY'),
                    },
                    {
                      text: adv.kindMonthly,
                      bold: scheduleKind === 'MONTHLY',
                      onClick: () => changeScheduleKind('MONTHLY'),
                    },
                  ])
                }
              />

              {scheduleKind === 'WEEKLY' && (
                <ListItem
                  title={adv.weekdayLabel}
                  after={t7.orderCapacity.dayNamesLong[scheduleDay[0] ?? 1]}
                  link="#"
                  onClick={() =>
                    openPicker(
                      adv.weekdayLabel,
                      WEEKDAY_CHOICES.map((day) => ({
                        text: t7.orderCapacity.dayNamesLong[day],
                        bold: scheduleDay[0] === day,
                        onClick: () => changeScheduleDay([day]),
                      })),
                    )
                  }
                />
              )}

              {scheduleKind === 'MONTHLY' && (
                <>
                  <ListItem
                    title={adv.monthDayLabel}
                    after={
                      scheduleDay.length > 0
                        ? [...scheduleDay].sort((a, b) => a - b).join(', ')
                        : '-'
                    }
                    link="#"
                    popoverOpen={`.month-day-popover-${target}`}
                  />
                  <Popover
                    className={`month-day-popover-${target}`}
                    style={{ width: 280 }}
                    onPopoverClosed={commitMonthDays}
                  >
                    <div style={{ padding: 12 }}>
                      <ChipMultiSelect
                        options={DAY_OF_MONTH_CHOICES.map(String)}
                        selected={scheduleDay.map(String)}
                        onToggle={(v) => toggleMonthDay(Number(v))}
                        getLabel={(v) => v}
                        columns={5}
                      />
                    </div>
                  </Popover>
                </>
              )}

              <ListItem title={adv.hourLabel}>
                <ChipInput
                  slot="after"
                  type="time"
                  value={`${String(scheduleHour).padStart(2, '0')}:00`}
                  step={60}
                  onChange={(v) => {
                    const h = parseInt(v.split(':')[0], 10);
                    if (!Number.isNaN(h)) changeScheduleHour(h);
                  }}
                />
              </ListItem>
            </List>
          </AccordionContent>
        </ListItem>
      </List>
      <BlockFooter>{t.advancedFooter}</BlockFooter>
      <BlockFooter>{adv.scheduleFooter}</BlockFooter>

      <BlockFooter>{t.cleanupReassurance}</BlockFooter>

      <PurgePreviewPopup
        opened={previewOpen}
        onClose={() => setPreviewOpen(false)}
        input={input}
        title={copy.previewTitle}
      />
    </>
  );
}

export default PurgeTargetSection;
